import { X } from 'lucide-react'

const RADIUS = 40
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export interface VideoUploadProgressProps {
    phase: 'uploading' | 'processing'
    progress: number
    onRemove: () => void
    aspectRatio?: { width: number; height: number }
}

export function VideoUploadProgress({
    phase,
    progress,
    onRemove,
    aspectRatio,
}: VideoUploadProgressProps) {
    const indeterminate = phase === 'uploading' && progress === 0
    const dashOffset = CIRCUMFERENCE * (1 - progress / 100)

    return (
        <div
            className="relative w-full h-48 bg-black rounded-lg overflow-hidden flex items-center justify-center border border-border"
            style={
                aspectRatio
                    ? { aspectRatio: `${aspectRatio.width}/${aspectRatio.height}` }
                    : undefined
            }
        >
            <div className="flex flex-col items-center gap-2">
                <svg
                    width="100"
                    height="100"
                    viewBox="0 0 100 100"
                    className={indeterminate ? 'animate-spin' : undefined}
                    style={indeterminate ? { transformOrigin: 'center' } : undefined}
                >
                    {/* Track circle */}
                    <circle
                        cx="50"
                        cy="50"
                        r={RADIUS}
                        fill="none"
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth="4"
                    />
                    {/* Progress arc */}
                    <circle
                        cx="50"
                        cy="50"
                        r={RADIUS}
                        fill="none"
                        stroke="white"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={indeterminate ? CIRCUMFERENCE * 0.75 : dashOffset}
                        transform="rotate(-90 50 50)"
                        style={
                            indeterminate
                                ? undefined
                                : { transition: 'stroke-dashoffset 0.3s ease' }
                        }
                    />
                </svg>
                <span className="text-white text-sm font-medium">
                    {phase === 'uploading' ? 'Uploading…' : 'Processing…'}
                </span>
            </div>

            <button
                type="button"
                onClick={onRemove}
                className="absolute top-2 right-2 p-1 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                aria-label="Remove video"
            >
                <X className="w-4 h-4 text-white" />
            </button>
        </div>
    )
}
